/**
 * The priority lane (JARVIS-126): the set of projects separated from the
 * rest, in `priorityLaneProjectIds` order. A horizontal strip of small tiles
 * with `#N` badges. Drop zones, and what each one runs:
 *
 *   on a lane tile      → reorderProjectPriority(source, target, side)
 *   on the lane itself  → addProjectToPriorityLane(source)
 *
 * Hover ◀ / ▶ on a tile promote/demote it one place — the same
 * reorderProjectPriority call with the neighbour as target.
 */
import { ThunderboltFilled } from "@ant-design/icons"
import { Typography } from "antd"
import { type DragEvent, useCallback, useState } from "react"
import styled from "styled-components"

import {
  getPriorityStep,
  PORTFOLIO_PRIORITY_LANE_MAX,
  type ProjectPlacement,
} from "#components/PortfolioManagement/portfolioArrangement"
import { type PortfolioMockProject } from "#components/PortfolioManagement/portfolioUiTypes"

import PortfolioProjectTile from "./PortfolioProjectTile"

const { Text } = Typography

const SLane = styled.div<{ $dropping: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px 10px 10px;
  border-radius: 10px;
  border: 2px dashed ${({ $dropping }) => ($dropping ? "#1677ff" : "#ffd591")};
  background: ${({ $dropping }) => ($dropping ? "#e6f4ff" : "#fffbe6")};
  transition:
    border-color 0.15s ease,
    background 0.15s ease;
`

const SHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const STiles = styled.div`
  display: flex;
  gap: 10px;
  overflow-x: auto;
  padding-bottom: 2px;
  min-height: 48px;
  align-items: stretch;
`

export interface PortfolioPriorityLaneProps {
  laneProjects: PortfolioMockProject[]
  laneIds: readonly string[]
  coverRotationFor: (project: PortfolioMockProject) => number
  activeProjectId: string | null
  dragProjectId: string | null
  onOpen: (projectId: string) => void
  onEdit: (projectId: string) => void
  onReorder: (
    sourceId: string,
    targetId: string,
    placement: ProjectPlacement
  ) => void
  onAdd: (projectId: string) => void
  onRemove: (projectId: string) => void
  onDragStart: (event: DragEvent<HTMLDivElement>, projectId: string) => void
  onDragEnd: () => void
}

const sideFromPointer = (event: DragEvent<HTMLElement>): ProjectPlacement => {
  const rect = event.currentTarget.getBoundingClientRect()
  return event.clientX < rect.left + rect.width / 2 ? "before" : "after"
}

const PortfolioPriorityLane = ({
  laneProjects,
  laneIds,
  coverRotationFor,
  activeProjectId,
  dragProjectId,
  onOpen,
  onEdit,
  onReorder,
  onAdd,
  onRemove,
  onDragStart,
  onDragEnd,
}: PortfolioPriorityLaneProps) => {
  const [laneHover, setLaneHover] = useState(false)
  const [insert, setInsert] = useState<{
    projectId: string
    side: ProjectPlacement
  } | null>(null)

  const dragIsLaneMember = !!dragProjectId && laneIds.includes(dragProjectId)
  const laneIsFull = laneIds.length >= PORTFOLIO_PRIORITY_LANE_MAX
  const canAcceptNew = !!dragProjectId && !dragIsLaneMember && !laneIsFull

  const step = useCallback(
    (projectId: string, direction: "promote" | "demote") => {
      const target = getPriorityStep(laneIds, projectId, direction)
      if (!target) return
      onReorder(projectId, target.targetId, target.placement)
    },
    [laneIds, onReorder]
  )

  const reset = () => {
    setLaneHover(false)
    setInsert(null)
  }

  return (
    <SLane
      $dropping={laneHover && canAcceptNew}
      data-testid="portfolio-lane"
      data-count={laneIds.length}
      onDragOver={(event) => {
        if (!canAcceptNew) return
        event.preventDefault()
        event.dataTransfer.dropEffect = "move"
        setLaneHover(true)
      }}
      onDragLeave={(event) => {
        if (event.currentTarget.contains(event.relatedTarget as Node | null))
          return
        setLaneHover(false)
      }}
      onDrop={(event) => {
        if (!dragProjectId || !canAcceptNew) return
        event.preventDefault()
        onAdd(dragProjectId)
        reset()
      }}
    >
      <SHeader>
        <ThunderboltFilled style={{ color: "#ad6800" }} />
        <Text strong style={{ fontSize: 13 }}>
          Priority lane
        </Text>
        <Text
          type="secondary"
          style={{ fontSize: 12 }}
          data-testid="portfolio-lane-count"
        >
          {laneIds.length}/{PORTFOLIO_PRIORITY_LANE_MAX}
        </Text>
        <Text type="secondary" style={{ fontSize: 12, marginLeft: "auto" }}>
          {laneProjects.length
            ? "Drag to reorder · hover a tile for ◀ ▶"
            : "Drag a project here, or use its ⚡"}
        </Text>
      </SHeader>
      <STiles>
        {laneProjects.map((project, index) => (
          <PortfolioProjectTile
            key={project.id}
            project={project}
            variant="lane"
            priorityIndex={index + 1}
            coverRotation={coverRotationFor(project)}
            isActive={activeProjectId === project.id}
            isDragging={dragProjectId === project.id}
            insertSide={insert?.projectId === project.id ? insert.side : null}
            onOpen={onOpen}
            onEdit={onEdit}
            onPromote={(id) => step(id, "promote")}
            onDemote={(id) => step(id, "demote")}
            onRemoveFromLane={onRemove}
            canPromote={index > 0}
            canDemote={index < laneProjects.length - 1}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            onDragOver={(event, targetId) => {
              // Only lane members reorder here; a new project drops on the
              // lane itself (the bubbling handler above adds it).
              if (!dragIsLaneMember || dragProjectId === targetId) return
              event.preventDefault()
              event.stopPropagation()
              event.dataTransfer.dropEffect = "move"
              setInsert({ projectId: targetId, side: sideFromPointer(event) })
            }}
            onDragLeave={(targetId) =>
              setInsert((prev) => (prev?.projectId === targetId ? null : prev))
            }
            onDrop={(event, targetId) => {
              if (
                !dragProjectId ||
                !dragIsLaneMember ||
                dragProjectId === targetId
              )
                return
              event.preventDefault()
              event.stopPropagation()
              onReorder(dragProjectId, targetId, sideFromPointer(event))
              reset()
            }}
          />
        ))}
      </STiles>
    </SLane>
  )
}

export default PortfolioPriorityLane
